import { rawPathBounds } from '~/lib/objectBounds'
import { quantize } from '~/lib/params'
import { HEIGHT, WIDTH } from '~/lib/stage'
import { stringToRawPath } from '~/lib/svg/paths'
import { currentVideoFit } from '~/lib/videoFit'
import display_object_schema from '~/schemas/display_object_schema'

/** The `_v` this step produces. Settings stamped below it carry `x`/`y` as the geometry's top-left corner. */
export const PIVOT_VERSION = 9

const PIVOT = 0.5
const MAX_DEPTH = 16
const RAD = Math.PI / 180

const propsOf = obj => display_object_schema.definitions[obj?.type]?.properties

// The schema no longer declares `video`; `videoToFill` reads its position after this step.
const positioned = obj => obj.type === 'video' || !!propsOf(obj)?.x

/**
 * Every declared number but `x`/`y` at the precision `conform` stores it — before the shift is computed from it.
 *
 * ⚠️ `conform` quantizes AFTER the chain runs (rotation to whole degrees, scale to 0.01, sizes to 1), so a shift computed from the raw value turns an object the renderer then draws at a different angle. Writing the quantized values back makes the `migrateSettings`-only path (examples, dev tools) render the same numbers too.
 */
const quantized = obj => {
	const props = propsOf(obj)
	if (!props) return obj
	let out = obj
	for (const key in obj) {
		const prop = props[key]
		// `x`/`y` are about to be replaced; rounding them here as well would round the result twice.
		if (!prop || typeof obj[key] !== 'number' || key === 'x' || key === 'y') continue
		const q = quantize(prop, obj[key])
		if (q === obj[key]) continue
		if (out === obj) out = { ...obj }
		out[key] = q
	}
	return out
}

/**
 * ORIGIN-MODEL bounds of a non-group object relative to its stored `x`/`y`, frozen here as `getObjectBounds` computed them before the pivot model. A migration step must not follow the live bounds code: that code now reads the pivot model, and would re-read stored pre-pivot data under the wrong rule.
 */
const storedLeafBox = obj => {
	const x = obj.x ?? 0
	const y = obj.y ?? 0
	const none = { x: 0, y: 0, width: 0, height: 0 }
	const extent = () => {
		const local = rawPathBounds(stringToRawPath(obj.data))
		return { local, maxX: Math.max(0, local.x + local.width), maxY: Math.max(0, local.y + local.height) }
	}
	switch (obj.type) {
		case 'rect':
		case 'circle': {
			if (!obj.data) return { x: 0, y: 0, width: obj.width ?? 0, height: obj.height ?? 0 }
			const { maxX, maxY } = extent()
			return { x: 0, y: 0, width: maxX, height: maxY }
		}
		// Scale-realized: `applyMirrorOffset` kept a mirrored image on its unmirrored rectangle.
		case 'image':
		case 'video': {
			// Only a video auto-framed: an image without `x`/`y` drew at its origin.
			const fit = obj.type === 'video' && obj.fit_to_frame !== false ? currentVideoFit() : null
			// An auto-framed video stored no corner; the fit's corner was where it rendered.
			const cx = obj.x === undefined && fit ? Math.round((WIDTH - fit.width) / 2) : x
			const cy = obj.y === undefined && fit ? Math.round((HEIGHT - fit.height) / 2) : y
			return {
				x: cx - x,
				y: cy - y,
				width: (obj.width ?? fit?.width ?? 0) * Math.abs(obj.scaleX ?? 1),
				height: (obj.height ?? fit?.height ?? 0) * Math.abs(obj.scaleY ?? 1),
			}
		}
		case 'brush': {
			if (!obj.data) return none
			const { local, maxX, maxY } = extent()
			if (obj.variableWidth) {
				const minX = Math.min(0, local.x)
				const minY = Math.min(0, local.y)
				return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
			}
			const sw = ((obj.minWidth || 0) + (obj.widthGrowth ?? obj.maxWidth ?? 8)) / 2
			return { x: -sw, y: -sw, width: maxX + sw * 2, height: maxY + sw * 2 }
		}
		// Geometry-owning: nothing offset a mirrored path, so its far edge landed on the near side.
		case 'path': {
			if (!obj.data) return none
			const { maxX, maxY } = extent()
			const sx = obj.scaleX ?? 1
			const sy = obj.scaleY ?? 1
			return {
				x: sx < 0 ? maxX * sx : 0,
				y: sy < 0 ? maxY * sy : 0,
				width: maxX * Math.abs(sx),
				height: maxY * Math.abs(sy),
			}
		}
		default:
			return { x: 0, y: 0, width: obj.width ?? 0, height: obj.height ?? 0 }
	}
}

/** The union of a group's children's ROTATED footprints in its own UNSCALED frame, as `[minX, minY, maxX, maxY]`, or null when it draws nothing. */
const childUnion = (obj, map, depth) => {
	let minX = Infinity,
		minY = Infinity,
		maxX = -Infinity,
		maxY = -Infinity
	if (depth < MAX_DEPTH) {
		for (const id of obj.children ?? []) {
			const child = map[id]
			if (!child || child === obj) continue
			for (const [px, py] of footprint(child, map, depth + 1)) {
				if (px < minX) minX = px
				if (px > maxX) maxX = px
				if (py < minY) minY = py
				if (py > maxY) maxY = py
			}
		}
	}
	return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : null
}

/**
 * The rectangle an object renders in, in its own frame: after its scale, before its rotation, relative to its stored `x`/`y`. ORIGIN MODEL — reads the data as written before this step.
 *
 * ⚠️ A GROUP's box is the union of its children's ROTATED footprints, not of their unrotated boxes. The footprint is where a child actually draws, which does not depend on what `x`/`y` name; an unrotated box does — the same rule reads a different region once `x`/`y` name the centre.
 */
export const localBox = (obj, map, depth = 0) => {
	if (obj?.type !== 'group') return storedLeafBox(obj ?? {})
	const u = childUnion(obj, map, depth)
	if (!u) return { x: 0, y: 0, width: 0, height: 0 }
	const [minX, minY, maxX, maxY] = u
	// Sign-aware: nothing offsets a mirrored group at render time, so its far edge lands on the near side.
	const sx = obj.scaleX ?? 1
	const sy = obj.scaleY ?? 1
	return {
		x: sx < 0 ? maxX * sx : minX * sx,
		y: sy < 0 ? maxY * sy : minY * sy,
		width: (maxX - minX) * Math.abs(sx),
		height: (maxY - minY) * Math.abs(sy),
	}
}

/** A group's centre in its own unscaled frame — the point its children are re-expressed about, so the group's local origin IS its pivot. */
export const groupCentre = (group, map) => {
	const u = childUnion(group, map, 0)
	return u ? { x: (u[0] + u[2]) / 2, y: (u[1] + u[3]) / 2 } : { x: 0, y: 0 }
}

/** The four corners of a child's box in its parent's frame. */
const footprint = (obj, map, depth) => {
	const b = localBox(obj, map, depth)
	const r = (obj.rotation ?? 0) * RAD
	const cos = Math.cos(r)
	const sin = Math.sin(r)
	const x = obj.x ?? 0
	const y = obj.y ?? 0
	return [
		[b.x, b.y],
		[b.x + b.width, b.y],
		[b.x + b.width, b.y + b.height],
		[b.x, b.y + b.height],
	].map(([lx, ly]) => [x + lx * cos - ly * sin, y + lx * sin + ly * cos])
}

/**
 * Where the pivot sits, in the object's parent frame: its box's centre, carried through its rotation.
 *
 * `x' = x + R(θ)·(box.x + ½·box.width, box.y + ½·box.height)`. The box's own offset matters — a brush's box starts a stroke half-width before `x`, a mirrored path's at `maxX·sx` — so the centre is never `R·(½w, ½h)` alone.
 */
export const pivotPosition = (obj, map) => {
	const b = localBox(obj, map)
	const lx = b.x + PIVOT * b.width
	const ly = b.y + PIVOT * b.height
	const r = (obj.rotation ?? 0) * RAD
	const cos = Math.cos(r)
	const sin = Math.sin(r)
	return { x: (obj.x ?? 0) + lx * cos - ly * sin, y: (obj.y ?? 0) + lx * sin + ly * cos }
}

/** An auto-framed video stores no `x`/`y`: `videoFit` derives them, so there is nothing to carry. */
const derivesPosition = obj => obj.type === 'video' && obj.fit_to_frame !== false

/**
 * `x`/`y` stop naming the geometry's top-left corner and name its pivot — the centre, `pivotX`/`pivotY` at their initial.
 *
 * ⚠️ THE ONE STEP GATED ON `_v`, and so the one exception to the chain's rule 1. It rewrites `x`/`y` into values a user could have typed, so no key says whether it ran: idempotence lives in the stamp alone. Only a NUMERIC `_v` below `PIVOT_VERSION` is shifted. A missing stamp is read as current — every stored row carries one (`pivotBaseline.test.js`), and the in-app paths rebuild settings objects, where a dropped stamp read as legacy would shift a live scene a second time on its next save.
 *
 * ⚠️ A GROUP's local origin becomes its pivot: its own `x`/`y` move to its footprint's centre, and each child is re-expressed about that centre (`groupCentre`), so a group renders with no Container pivot and a child edit never moves its siblings. A group's `pivotX`/`pivotY` are not read.
 *
 * Every shift is computed from ONE snapshot of the incoming objects, so the order objects are visited in cannot matter: a group's box comes from its children's footprints as they were written, never from children already re-expressed.
 */
export function originToPivot(settings) {
	const v = settings?._v
	if (typeof v !== 'number' || v >= PIVOT_VERSION) return settings
	const objects = settings.objects
	if (!objects || typeof objects !== 'object') return settings

	const snapshot = {}
	for (const [id, obj] of Object.entries(objects))
		snapshot[id] = obj && typeof obj === 'object' ? quantized(obj) : obj

	const centres = new Map()
	const centreOf = group => {
		if (!centres.has(group)) centres.set(group, groupCentre(group, snapshot))
		return centres.get(group)
	}

	const next = {}
	for (const [id, obj] of Object.entries(snapshot)) {
		next[id] = obj
		if (!obj || typeof obj !== 'object' || id === 'root' || !positioned(obj) || derivesPosition(obj)) continue
		const p = pivotPosition(obj, snapshot)
		const parent = snapshot[obj.parentId]
		const c = parent?.type === 'group' && parent !== obj ? centreOf(parent) : { x: 0, y: 0 }
		next[id] = { ...obj, x: p.x - c.x, y: p.y - c.y }
	}
	return { ...settings, objects: next }
}

export default originToPivot
